import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Grid,
  Switch,
  FormControlLabel,
  Alert,
  CircularProgress,
  Snackbar,
  Divider,
  Card,
  CardContent,
  Chip
} from '@mui/material';
import {
  Save as SaveIcon,
  Notifications as NotificationsIcon,
  Email as EmailIcon,
  PlayArrow as PlayIcon
} from '@mui/icons-material';
import { configService } from '../services/configService';

function Configuration() {
  const [config, setConfig] = useState({
    pendingSignatureThreshold: 20,
    pendingSignatureEmail: '',
    pendingTransferThreshold: 15,
    pendingTransferEmail: '',
    pendingPaymentThreshold: 15,
    pendingPaymentEmail: '',
    executionTime: '09:00',
    isActive: true
  });
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [errors, setErrors] = useState({});
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  useEffect(() => {
    loadConfig();
  }, []);

  const loadConfig = async () => {
    setLoading(true);
    try {
      const result = await configService.getNotificationConfig();
      
      if (result.success) {
        // Convertir isActive de número a booleano si es necesario
        const configData = {
          ...result.data,
          isActive: Boolean(result.data.isActive)
        };
        setConfig(configData);
      } else {
        showSnackbar('Error al cargar configuración: ' + result.message, 'error');
      }
    } catch (error) {
      showSnackbar('Error al cargar configuración', 'error');
    } finally {
      setLoading(false);
    }
  };

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleChange = (field, value) => {
    setConfig(prev => ({ ...prev, [field]: value }));
    
    // Limpiar error del campo
    if (errors[field]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (config.pendingSignatureThreshold < 1) {
      newErrors.pendingSignatureThreshold = 'Debe ser mayor a 0';
    }
    
    if (!config.pendingSignatureEmail || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(config.pendingSignatureEmail)) {
      newErrors.pendingSignatureEmail = 'Email inválido';
    }
    
    if (config.pendingTransferThreshold < 1) {
      newErrors.pendingTransferThreshold = 'Debe ser mayor a 0';
    }
    
    if (!config.pendingTransferEmail || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(config.pendingTransferEmail)) {
      newErrors.pendingTransferEmail = 'Email inválido';
    }
    
    if (config.pendingPaymentThreshold < 1) {
      newErrors.pendingPaymentThreshold = 'Debe ser mayor a 0';
    }
    
    if (!config.pendingPaymentEmail || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(config.pendingPaymentEmail)) {
      newErrors.pendingPaymentEmail = 'Email inválido';
    }
    
    if (!config.executionTime || !/^([0-1]?[0-9]|2[0-3]):[0-5][0-9]$/.test(config.executionTime)) {
      newErrors.executionTime = 'Formato de hora inválido (HH:mm)';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validateForm()) {
      showSnackbar('Por favor, corrija los errores en el formulario', 'error');
      return;
    }
    
    setSaving(true);
    try {
      // Asegurar que isActive sea booleano
      const configToSave = {
        ...config,
        isActive: Boolean(config.isActive)
      };
      
      const result = await configService.updateNotificationConfig(configToSave);
      
      if (result.success) {
        showSnackbar('Configuración guardada correctamente', 'success');
        // Convertir isActive en la respuesta también
        const updatedConfig = {
          ...result.data,
          isActive: Boolean(result.data.isActive)
        };
        setConfig(updatedConfig);
      } else {
        showSnackbar('Error al guardar configuración: ' + result.message, 'error');
      }
    } catch (error) {
      showSnackbar('Error al guardar configuración', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleTestNotifications = async () => {
    setTesting(true);
    try {
      const result = await configService.checkNotifications();
      
      if (result.success) {
        const { notificationsSent, counts } = result.data;
        
        if (notificationsSent > 0) {
          showSnackbar(
            `Se enviaron ${notificationsSent} notificación(es). Estados: Firma: ${counts.pendingSignature}, Transferencia: ${counts.pendingTransfer}, Pago: ${counts.pendingPayment}`,
            'success'
          );
        } else {
          showSnackbar(
            `No se enviaron notificaciones. Estados: Firma: ${counts.pendingSignature}, Transferencia: ${counts.pendingTransfer}, Pago: ${counts.pendingPayment}`,
            'info'
          );
        }
      } else {
        showSnackbar('Error al verificar notificaciones: ' + result.message, 'error');
      }
    } catch (error) {
      showSnackbar('Error al verificar notificaciones', 'error');
    } finally {
      setTesting(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ padding: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4">
          <NotificationsIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
          Configuración de Notificaciones
        </Typography>
        <FormControlLabel
          control={
            <Switch
              checked={config.isActive}
              onChange={(e) => handleChange('isActive', e.target.checked)}
              color="primary"
            />
          }
          label={config.isActive ? 'Notificaciones Activas' : 'Notificaciones Inactivas'}
        />
      </Box>

      <Alert severity="info" sx={{ mb: 3 }}>
        El sistema verificará automáticamente la cantidad de pedidos en cada estado y enviará notificaciones
        por correo electrónico cuando se superen los umbrales configurados.
      </Alert>

      <Grid container spacing={3}>
        {/* Configuración de Pendiente de Firma */}
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Chip label="Pendiente de Firma" color="warning" sx={{ mr: 1 }} />
              </Box>
              
              <TextField
                fullWidth
                type="number"
                label="Umbral de Alerta"
                value={config.pendingSignatureThreshold}
                onChange={(e) => handleChange('pendingSignatureThreshold', parseInt(e.target.value) || 0)}
                error={!!errors.pendingSignatureThreshold}
                helperText={errors.pendingSignatureThreshold || 'Número de pedidos para enviar alerta'}
                sx={{ mb: 2 }}
                InputProps={{ inputProps: { min: 1 } }}
              />
              
              <TextField
                fullWidth
                type="email"
                label="Email de Notificación"
                value={config.pendingSignatureEmail}
                onChange={(e) => handleChange('pendingSignatureEmail', e.target.value)}
                error={!!errors.pendingSignatureEmail}
                helperText={errors.pendingSignatureEmail || 'Email que recibirá las alertas'}
                InputProps={{
                  startAdornment: <EmailIcon sx={{ mr: 1, color: 'text.secondary' }} />
                }}
              />
            </CardContent>
          </Card>
        </Grid>

        {/* Configuración de Pendiente de Transferencia */}
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Chip label="Pendiente de Transferencia" color="info" sx={{ mr: 1 }} />
              </Box>
              
              <TextField
                fullWidth
                type="number"
                label="Umbral de Alerta"
                value={config.pendingTransferThreshold}
                onChange={(e) => handleChange('pendingTransferThreshold', parseInt(e.target.value) || 0)}
                error={!!errors.pendingTransferThreshold}
                helperText={errors.pendingTransferThreshold || 'Número de pedidos para enviar alerta'}
                sx={{ mb: 2 }}
                InputProps={{ inputProps: { min: 1 } }}
              />
              
              <TextField
                fullWidth
                type="email"
                label="Email de Notificación"
                value={config.pendingTransferEmail}
                onChange={(e) => handleChange('pendingTransferEmail', e.target.value)}
                error={!!errors.pendingTransferEmail}
                helperText={errors.pendingTransferEmail || 'Email que recibirá las alertas'}
                InputProps={{
                  startAdornment: <EmailIcon sx={{ mr: 1, color: 'text.secondary' }} />
                }}
              />
            </CardContent>
          </Card>
        </Grid>

        {/* Configuración de Pendiente de Pago */}
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Chip label="Pendiente de Pago" color="warning" sx={{ mr: 1 }} />
              </Box>
              
              <TextField
                fullWidth
                type="number"
                label="Umbral de Alerta"
                value={config.pendingPaymentThreshold}
                onChange={(e) => handleChange('pendingPaymentThreshold', parseInt(e.target.value) || 0)}
                error={!!errors.pendingPaymentThreshold}
                helperText={errors.pendingPaymentThreshold || 'Número de pedidos para enviar alerta'}
                sx={{ mb: 2 }}
                InputProps={{ inputProps: { min: 1 } }}
              />
              
              <TextField
                fullWidth
                type="email"
                label="Email de Notificación"
                value={config.pendingPaymentEmail}
                onChange={(e) => handleChange('pendingPaymentEmail', e.target.value)}
                error={!!errors.pendingPaymentEmail}
                helperText={errors.pendingPaymentEmail || 'Email que recibirá las alertas'}
                InputProps={{
                  startAdornment: <EmailIcon sx={{ mr: 1, color: 'text.secondary' }} />
                }}
              />
            </CardContent>
          </Card>
        </Grid>

        {/* Configuración General */}
        <Grid item xs={12}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Configuración General
            </Typography>
            <Divider sx={{ mb: 3 }} />
            
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  type="time"
                  label="Hora de Verificación Diaria"
                  value={config.executionTime || '09:00'}
                  onChange={(e) => handleChange('executionTime', e.target.value)}
                  error={!!errors.executionTime}
                  helperText={errors.executionTime || 'Hora del día para verificar y enviar notificaciones'}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              
              <Grid item xs={12} md={6}>
                <Alert severity="info">
                  La verificación automática se ejecutará diariamente a la hora configurada.
                  También puedes probar el envío de notificaciones manualmente.
                </Alert>
              </Grid>
            </Grid>
          </Paper>
        </Grid>
      </Grid>

      <Box sx={{ mt: 3, display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
        <Button
          variant="outlined"
          startIcon={testing ? <CircularProgress size={20} /> : <PlayIcon />}
          onClick={handleTestNotifications}
          disabled={testing || saving || !config.isActive}
        >
          {testing ? 'Verificando...' : 'Probar Notificaciones'}
        </Button>
        
        <Button
          variant="contained"
          color="primary"
          startIcon={saving ? <CircularProgress size={20} /> : <SaveIcon />}
          onClick={handleSave}
          disabled={saving || testing}
        >
          {saving ? 'Guardando...' : 'Guardar Configuración'}
        </Button>
      </Box>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default Configuration;
